import type { ScheduleAdditionalInfo, ScheduleTab } from '../models/schedule.types'

// Ports v3's `OrphexAdditionalInfoTitle.vue` `getRawInfo` (ln 39-119): the section's tooltip
// tells the viewer, per data source, when that partition was last synced. `additional_info.tabs`
// maps a tab name to its partition; `additional_info.raw_data_last_modified` carries one
// freshness stamp per (partition, data_source) pair. A data source can span several partitions
// (and several tabs), so entries are grouped by `data_source` and only the newest `last_modified`
// among its matched partitions survives.

export interface DataSyncEntry {
  dataSource: string
  lastModified: string
}

/**
 * Resolve the freshness stamps for a section's active tabs. `tabs` is the tab-name set the
 * section is currently showing (e.g. `activeTab.tabs`); entries whose partition isn't reachable
 * from one of those tabs are dropped, matching v3's `partitionToTabs` filter.
 */
export function resolveDataSyncEntries(
  additionalInfo: ScheduleAdditionalInfo | null | undefined,
  tabs: readonly string[],
): DataSyncEntry[] {
  const scheduleTabs = additionalInfo?.tabs
  const rawData = additionalInfo?.raw_data_last_modified
  if (!scheduleTabs || !tabs.length || !rawData?.length) return []

  const reachablePartitions = new Set<string>()
  for (const tabName of tabs) {
    const tab = scheduleTabs[tabName]
    const partitionName = isTabDetail(tab) ? tab.partition_name : undefined
    if (partitionName) reachablePartitions.add(partitionName)
  }
  if (!reachablePartitions.size) return []

  const latestByDataSource = new Map<string, number>()
  for (const entry of rawData) {
    if (!reachablePartitions.has(entry.partition_name)) continue
    const timestamp = Date.parse(entry.last_modified)
    if (Number.isNaN(timestamp)) continue
    const current = latestByDataSource.get(entry.data_source)
    if (current === undefined || timestamp > current) {
      latestByDataSource.set(entry.data_source, timestamp)
    }
  }

  return [...latestByDataSource.entries()].map(([dataSource, timestamp]) => ({
    dataSource,
    lastModified: new Date(timestamp).toISOString(),
  }))
}

function isTabDetail(value: unknown): value is ScheduleTab {
  return typeof value === 'object' && value !== null
}
